import { useState, useEffect, useMemo, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  PenLine, ImageIcon, Film, Flame, ArrowRight, Wand2, LayoutGrid, Users,
  Clapperboard, Camera, Sun, Cloud, House, FolderOpen, Sparkles, LogOut, Settings,
} from "lucide-react";
import { Sparkle } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_MARK } from "@/lib/brand";
import BottomNav from "@/components/BottomNav";
import NewStoryModal from "@/components/NewStoryModal";
import ImageGenModal from "@/components/ImageGenModal";
import VideoGenModal from "@/components/VideoGenModal";
import LoopVideo from "@/components/LoopVideo";

const HERO_IMG = "https://images.unsplash.com/photo-1597839219216-a773cb2473e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjBkYXJrJTIwYW5pbWF0aW9uJTIwZnJhbWV8ZW58MHx8fHwxNzg0MzkyNDA3fDA&ixlib=rb-4.1.0&q=85";
const FEATURE_IMG = "https://images.unsplash.com/photo-1499428665502-503f6c608263?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxjaW5lbWF0aWMlMjBkYXJrJTIwYW5pbWF0aW9uJTIwZnJhbWV8ZW58MHx8fHwxNzg0MzkyNDA3fDA&ixlib=rb-4.1.0&q=85";

const ACTIONS = [
  { m: "story", icon: PenLine, label: "Astro Reference", sub: "Coming soon", desc: "Storyboard your reference into a full directed sequence. Launching soon." },
  { m: "image", icon: ImageIcon, label: "Image", sub: "Generate art", desc: "Create high-quality frames with reference-locked consistency." },
  { m: "video", icon: Film, label: "Animation", sub: "Bring to life", desc: "Animate any frame into motion with SeeDance 2.5." },
];

const FEATURED = [
  { img: FEATURE_IMG, tag: "Director's Panels", meta: "Storyboard · 8 scenes", span: true },
  { img: "https://images.pexels.com/photos/14415267/pexels-photo-14415267.jpeg?auto=compress&cs=tinysrgb&w=800", tag: "Crimson Wanderer", meta: "Character · Image" },
  { img: "https://images.pexels.com/photos/20278554/pexels-photo-20278554.jpeg?auto=compress&cs=tinysrgb&w=800", tag: "Neon Alley", meta: "Environment · Animation" },
];

const TREND = [
  { img: "https://images.unsplash.com/photo-1639628735078-ed2f038a193e?w=600&q=75", tag: "3D Character", hot: true },
  { img: "https://images.unsplash.com/photo-1677143016687-8dbb7e71db08?w=600&q=75", tag: "Neon Portrait" },
  { img: "https://images.pexels.com/photos/31002082/pexels-photo-31002082.jpeg?auto=compress&cs=tinysrgb&w=600", tag: "Anime Signs", hot: true },
  { img: "https://images.unsplash.com/photo-1637858868799-7f26a0640eb6?w=600&q=75", tag: "Meme Toon" },
  { img: "https://images.unsplash.com/flagged/photo-1579451443170-44b3963c3341?w=600&q=75", tag: "Cyberpunk" },
  { img: "https://images.unsplash.com/photo-1740252117012-bb53ad05e370?w=600&q=75", tag: "Cartoon" },
  { img: "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=600&q=75", tag: "Storyboard", hot: true },
  { img: "https://images.unsplash.com/photo-1587930734782-4fe289d9ea8f?w=600&q=75", tag: "AI Edit" },
];

const HIGHLIGHTS = [
  { icon: PenLine, title: "Story In, Scenes Out", desc: "Write a story. nosey breaks it into cinematic beats automatically — no prompting required." },
  { icon: LayoutGrid, title: "Director's Panels", desc: "Every scene becomes a panel with camera angle, emotion, lighting, weather & dialogue." },
  { icon: Wand2, title: "AI Director", desc: "Say 'make it more dramatic' or 'zoom in' — every panel rewrites itself instantly." },
  { icon: ImageIcon, title: "Consistent Images", desc: "Reference-locked generation keeps faces, clothes and colors consistent across frames." },
  { icon: Users, title: "Reference Intelligence", desc: "Auto-classified references guide identity, style & environment without hallucinating props." },
  { icon: Clapperboard, title: "Built for Animators", desc: "A storytelling-first pipeline — think like a filmmaker, not a prompt engineer." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

function Banner({ b, go, testid }) {
  return (
    <button onClick={() => b.link && go(b.link)} data-testid={testid}
      className={`block w-full relative rounded-2xl overflow-hidden border border-white/10 aspect-[16/6] group ${b.link ? "cursor-pointer" : "cursor-default"}`}>
      <img src={b.img} alt={b.caption || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink-950/80 to-transparent" />
      {b.caption && <div className="absolute inset-0 flex items-center px-6 sm:px-10"><span className="font-heading font-black uppercase tracking-tight text-xl sm:text-3xl max-w-lg text-left">{b.caption}</span></div>}
    </button>
  );
}

function HomeSlideshow({ slides, onGo }) {
  const [i, setI] = useState(0);
  const n = slides.length;
  useEffect(() => {
    if (n <= 1) return;
    const t = setInterval(() => setI((x) => (x + 1) % n), 5000);
    return () => clearInterval(t);
  }, [n]);
  if (!n) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8" data-testid="home-slideshow">
      <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[16/9] sm:aspect-[21/9] bg-ink-900">
        {slides.map((s, idx) => (
          <div key={idx} className={`absolute inset-0 transition-opacity duration-700 ${idx === i ? "opacity-100" : "opacity-0 pointer-events-none"}`} data-testid={`slide-${idx}`}>
            {s.img && <img src={s.img} alt={s.title || ""} className="w-full h-full object-cover" />}
            <div className="absolute inset-0 bg-gradient-to-r from-ink-950/90 via-ink-950/40 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-12 max-w-2xl">
              {s.title && <h2 className="font-heading font-black uppercase tracking-tight text-2xl sm:text-4xl lg:text-5xl">{s.title}</h2>}
              {s.subtitle && <p className="mt-2 sm:mt-3 text-[#D8CFC2] text-sm sm:text-lg max-w-md">{s.subtitle}</p>}
              {s.cta_label && (
                <button onClick={() => onGo(s.link || "/home")} data-testid={`slide-${idx}-cta`}
                  className="mt-4 sm:mt-6 self-start inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-gold text-ink-950 font-heading font-bold text-xs sm:text-sm rounded-md hover:bg-goldlight transition-colors">
                  {s.cta_label} <ArrowRight size={16} />
                </button>
              )}
            </div>
          </div>
        ))}
        {n > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {slides.map((_, idx) => (
              <button key={idx} onClick={() => setI(idx)} data-testid={`slide-dot-${idx}`}
                className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-gold" : "w-1.5 bg-white/40"}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAuthed = !!user;
  const [modal, setModal] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    api.get("/site-config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, []);

  const heroImg = cfg?.hero_image || HERO_IMG;
  const scattered = useMemo(() => {
    const imgs = [
      ...((cfg?.featured || []).map((f) => ({ img: f.img, tag: f.tag }))),
      ...((cfg?.trending || []).map((t) => ({ img: t.img, tag: t.tag }))),
    ].filter((x) => x.img);
    return imgs.map((x) => ({ ...x, r: Math.random() })).sort((a, b) => a.r - b.r);
  }, [cfg]);
  const trendingTpl = templates.filter((t) => t.trending);
  const presentCats = [...new Set(templates.map((t) => t.category))].filter((c) => c && c !== "Trending");
  const orderRef = (cfg?.template_categories || []).filter((c) => c !== "Trending");
  const orderedCats = [
    ...orderRef.filter((c) => presentCats.includes(c)),
    ...presentCats.filter((c) => !orderRef.includes(c)),
  ];
  const rows = [
    ...(trendingTpl.length ? [{ name: "Trending", items: trendingTpl }] : []),
    ...orderedCats.map((c) => ({ name: c, items: templates.filter((t) => t.category === c) })),
  ];
  const slides = cfg?.home_slides || [];
  const banners = (cfg?.home_banners || []).filter((b) => b.img);


  const create = (m) => {
    if (m === "story") { toast("🚀 Astro Reference (Storyboard) is coming soon", { description: "Turn any reference into a full directed sequence — launching shortly." }); return; }
    if (!isAuthed) { navigate("/login?redirect=/home"); return; }
    setModal(m);
  };
  const go = (path) => { if (!isAuthed) { navigate(`/login?redirect=${encodeURIComponent(path)}`); return; } navigate(path); };

  return (
    <div className={`w-full min-h-screen bg-ink-950 text-[#F0E9DD] overflow-x-hidden ${isAuthed ? "pb-24" : ""}`}>
      {/* HEADER — clean black */}
      <header className="bg-ink-950 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_MARK} alt="nosey ai" className="w-8 h-8 sm:w-9 sm:h-9 object-contain" />
            <span className="font-heading font-bold tracking-tight text-xl sm:text-2xl">nosey<span className="text-gold"> ai</span></span>
          </div>
          {isAuthed ? (
            <div className="flex items-center gap-2">
              {user?.role === "admin" && (
                <button onClick={() => navigate("/admin")} data-testid="home-admin-btn"
                  className="flex items-center gap-1.5 border border-gold/40 bg-[#1a140d] rounded-full px-3 py-1.5 sm:py-2 hover:border-gold transition-colors" title="Admin panel">
                  <Settings size={15} className="text-gold" />
                  <span className="hidden sm:inline font-mono text-xs text-gold font-semibold">Admin</span>
                </button>
              )}
              <button onClick={() => navigate("/pricing")} data-testid="home-credits"
                className="flex items-center gap-1.5 border border-gold/40 bg-[#1a140d] rounded-full px-3 py-1.5 sm:py-2 hover:border-gold transition-colors">
                <Sparkle size={14} className="text-gold" />
                <span className="font-mono text-xs sm:text-sm text-gold font-semibold">{user?.role === "admin" ? "∞" : (user?.credits ?? 0)}</span>
              </button>
            </div>
          ) : (
            <button onClick={() => navigate("/login")} data-testid="home-enter-studio"
              className="px-4 sm:px-5 py-2 sm:py-2.5 bg-gold text-ink-950 font-heading font-bold text-xs sm:text-sm hover:bg-goldlight transition-colors rounded-md">
              Enter Studio
            </button>
          )}
        </div>
      </header>

      {/* TOP SLIDESHOW — admin customizable */}
      <HomeSlideshow slides={slides} onGo={go} />

      {/* TEMPLATE DISCOVERY — AI App Store */}
      {rows.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="flex items-center gap-2 mb-2">
            <Flame size={22} className="text-gold" />
            <h2 className="font-heading font-black uppercase tracking-tight text-2xl sm:text-4xl">Explore AI Templates</h2>
          </div>
          <p className="text-[#A89F94] text-sm mb-8">Pick a look, upload your photo, tap once. That's it.</p>

          <div className="space-y-10">
            {rows.map((row, ri) => (
              <Fragment key={row.name}>
              <div>
                <div className="flex items-end justify-between mb-4">
                  <h3 className="font-heading font-bold text-xl sm:text-2xl flex items-center gap-2">
                    {row.name === "Trending" && <Flame size={18} className="text-gold" />}{row.name}
                  </h3>
                </div>
                <div className="flex gap-4 overflow-x-auto no-scrollbar -mx-1 px-1 pb-2 snap-x">
                  {row.items.map((t) => (
                    <button key={t.id} onClick={() => navigate(`/t/${t.id}`)} data-testid={`template-card-${t.id}`}
                      className="group relative shrink-0 w-[40vw] max-w-[230px] sm:w-52 snap-start rounded-2xl overflow-hidden border border-white/10 bg-ink-900 text-left hover:border-gold/60 transition-colors">
                      <div className="relative aspect-[3/4] overflow-hidden bg-ink-950">
                        {t.preview_video
                          ? <LoopVideo src={t.preview_video} autoplay={!!t.loop_on_home} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                          : <img src={t.thumbnail} alt={t.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />}
                        <div className="absolute inset-0 bg-gradient-to-t from-ink-950/95 via-transparent to-transparent" />
                        <div className="absolute top-2 left-2 flex gap-1">
                          {t.trending && <span className="bg-gold text-ink-950 text-[9px] font-bold rounded-full px-2 py-0.5 flex items-center gap-1"><Flame size={9} /> HOT</span>}
                          {t.output_type === "video" && <span className="bg-ink-950/80 text-[#F0E9DD] text-[9px] font-bold rounded-full px-2 py-0.5 flex items-center gap-1"><Film size={9} /> Video</span>}
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="font-heading font-bold text-sm truncate">{t.title}</div>
                        {t.description && <div className="text-[11px] text-[#A89F94] line-clamp-2 mt-0.5">{t.description}</div>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              {((ri + 1) % 5 === 0) && banners[Math.floor(ri / 5)] && (
                <Banner b={banners[Math.floor(ri / 5)]} go={go} testid={`home-banner-${Math.floor(ri / 5)}`} />
              )}
              </Fragment>
            ))}
            {banners.slice(Math.floor(rows.length / 5)).map((b, bi) => (
              <Banner key={`tb-${bi}`} b={b} go={go} testid={`home-banner-${Math.floor(rows.length / 5) + bi}`} />
            ))}
          </div>
        </section>
      )}

      {/* PRIMARY ACTIONS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="label-mono mb-6 flex items-center gap-3"><span className="w-8 h-px bg-gold" /> Or start from scratch</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          {ACTIONS.map((a, i) => (
            <motion.button key={a.m} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} transition={{ delay: i * 0.08 }}
              onClick={() => create(a.m)} data-testid={`home-action-${a.m}`}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-ink-900/60 backdrop-blur-xl p-6 text-left hover:border-gold/60 transition-colors focus:outline-none focus:ring-2 focus:ring-gold/50">
              <div className="w-12 h-12 rounded-xl bg-[#1a140d] border border-gold/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                <a.icon size={24} className="text-gold" />
              </div>
              <div className="mt-4 flex items-center gap-2">
                <h3 className="font-heading font-bold text-lg">{a.label}</h3>
                <span className="text-[10px] font-mono uppercase tracking-wider text-gold/70">{a.sub}</span>
              </div>
              <p className="mt-1.5 text-sm text-[#A89F94] leading-relaxed">{a.desc}</p>
              <ArrowRight size={18} className="mt-4 text-gold opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </motion.button>
          ))}
        </div>
      </section>

      {/* ONE-CLICK FLAGSHIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="rounded-2xl border border-gold/40 bg-[#140f0a] p-6 sm:p-10 lg:p-12 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
          <div className="max-w-xl">
            <div className="label-mono mb-3 flex items-center gap-2"><Sparkle size={14} className="text-gold" /> Flagship Feature</div>
            <h2 className="font-heading font-black uppercase tracking-tight text-2xl sm:text-4xl">One-Click Animation</h2>
            <p className="mt-4 text-[#A89F94] leading-relaxed text-sm sm:text-base">
              Type one line. nosey writes the screenplay, breaks it into panels, sets every camera angle,
              emotion, light and line of dialogue, then renders all the frames — automatically.
            </p>
            <button onClick={() => create("story")} data-testid="flagship-start"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-gold text-ink-950 font-heading font-bold hover:bg-goldlight transition-colors rounded-md text-sm">
              Try it now <ArrowRight size={16} />
            </button>
          </div>
          <div className="font-mono text-sm text-[#A89F94] space-y-2 shrink-0">
            {["Write screenplay", "Break into panels", "Direct camera & mood", "Generate all frames", "Animate (SeeDance 2.5)"].map((s, i) => (
              <div key={s} className="flex items-center gap-3">
                <span className="w-6 h-6 border border-gold/50 text-gold flex items-center justify-center text-xs rounded">{i + 1}</span>{s}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCATTERED HOME IMAGES — admin customizable (Homepage Images) */}
      {scattered.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="flex items-center gap-2 mb-6">
            <Sparkle size={20} className="text-gold" />
            <h2 className="font-heading font-black uppercase tracking-tight text-2xl sm:text-3xl">Fresh from the studio</h2>
          </div>
          <div className="columns-2 sm:columns-3 lg:columns-4 gap-4" style={{ columnFill: "balance" }}>
            {scattered.map((im, i) => (
              <div key={i} className="mb-4 break-inside-avoid rounded-xl overflow-hidden border border-white/10 group" data-testid={`home-image-${i}`}>
                <img src={im.img} alt={im.tag || ""}
                  className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  style={{ aspectRatio: String([1, 0.72, 1.35, 1.1, 0.85, 1.5][i % 6]) }} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="rounded-2xl border border-white/10 bg-ink-900 p-8 sm:p-14 flex flex-col items-start">
          <h2 className="font-heading font-black uppercase tracking-tighter text-2xl sm:text-5xl max-w-2xl">
            Your idea is the <span className="text-gold">first frame.</span>
          </h2>
          <p className="mt-4 text-[#A89F94] max-w-lg text-sm sm:text-base">Write it once. Let nosey direct the rest.</p>
          <button onClick={() => create("story")} data-testid="cta-start"
            className="mt-7 flex items-center gap-2 px-7 py-4 bg-gold text-ink-950 font-heading font-bold hover:bg-goldlight transition-colors rounded-md">
            {isAuthed ? "Open the Studio" : "Enter the Studio"} <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* FOOTER NAV — in-flow, not sticky */}
      <footer className="bg-ink-900 border-t border-white/10 mt-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img src={LOGO_MARK} alt="nosey ai" className="w-8 h-8 object-contain" />
                <span className="font-heading font-bold tracking-tight text-xl">nosey<span className="text-gold"> ai</span></span>
              </div>
              <p className="text-sm text-[#A89F94] max-w-xs leading-relaxed">Storytelling-first animation pre-production. Direct your story, frame by frame.</p>
            </div>

            <nav className="flex flex-wrap items-center gap-x-8 gap-y-4">
              <button onClick={() => navigate("/home")} data-testid="footer-home" className="flex items-center gap-2 text-sm text-[#F0E9DD] hover:text-gold transition-colors"><House size={18} /> Home</button>
              <button onClick={() => go("/creations")} data-testid="footer-creations" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-gold transition-colors"><FolderOpen size={18} /> Creations</button>
              <button onClick={() => go("/pricing")} data-testid="footer-upgrade" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-gold transition-colors"><Sparkles size={18} /> Upgrade</button>
              {isAuthed
                ? <button onClick={() => { logout(); window.location.href = "/"; }} data-testid="footer-signout" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-gold transition-colors"><LogOut size={18} /> Sign out</button>
                : <button onClick={() => navigate("/login")} data-testid="footer-login" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-gold transition-colors"><ArrowRight size={18} /> Enter Studio</button>}
            </nav>
          </div>
          <div className="mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 label-mono">
            <span>nosey ai — Storytelling-first generation</span>
            <span>AI-generated content. Illegal use prohibited.</span>
          </div>
        </div>
      </footer>

      {/* Floating toolbar */}
      <div className={`fixed left-1/2 -translate-x-1/2 z-40 ${isAuthed ? "bottom-24" : "bottom-6"}`} data-testid="floating-toolbar">
        <div className="flex items-center gap-1 bg-ink-900/90 backdrop-blur-xl border border-white/10 rounded-full p-1.5 shadow-2xl shadow-black/60">
          {ACTIONS.map((a) => (
            <button key={a.m} onClick={() => create(a.m)} data-testid={`toolbar-${a.m}`}
              className="group flex items-center gap-2 px-4 py-2.5 rounded-full text-[#F0E9DD] hover:bg-gold hover:text-ink-950 transition-colors">
              <a.icon size={18} className="text-gold group-hover:text-ink-950 transition-colors" />
              <span className="text-sm font-heading font-bold hidden sm:inline">{a.label}</span>
            </button>
          ))}
        </div>
      </div>

      <NewStoryModal open={modal === "story"} onOpenChange={(o) => !o && setModal(null)} />
      <ImageGenModal open={modal === "image"} onOpenChange={(o) => !o && setModal(null)} onDone={() => navigate("/creations")} />
      <VideoGenModal open={modal === "video"} onOpenChange={(o) => !o && setModal(null)} onDone={() => navigate("/creations")} />

      {isAuthed && <BottomNav />}
    </div>
  );
}
